/**
 * money.ts — Tipo Money [CORE]
 *
 * Regra de ouro: todo valor monetário é representado como inteiro
 * em centavos com moeda explícita. NUNCA float.
 *
 * Referência: §0 modelo de dados (regra de ouro Peter/ECOSSISTEMA)
 * e §8.3 (FINANCEIRO.md §8.3).
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// Money — inteiro em centavos + moeda (NUNCA float)
// ---------------------------------------------------------------------------

export const MoneySchema = z.object({
  /**
   * Valor em centavos (inteiro). Exemplos:
   *   R$ 1,00 → 100
   *   R$ 1.234,56 → 123456
   * Negativo = saída (despesa).
   */
  centavos: z.number().int("Money.centavos deve ser inteiro — nunca float"),
  /** Código de moeda ISO 4217. Ex.: "BRL", "USD". */
  currency: z.string().length(3),
});

export type Money = z.infer<typeof MoneySchema>;

// ---------------------------------------------------------------------------
// Utilitários puros (sem efeitos colaterais)
// ---------------------------------------------------------------------------

/** Cria um valor Money em BRL a partir de centavos inteiros */
export function brl(centavos: number): Money {
  if (!Number.isInteger(centavos)) {
    throw new Error(
      `Money.centavos deve ser inteiro. Recebido: ${String(centavos)}. Use Math.round() antes de criar Money.`,
    );
  }
  return { centavos, currency: "BRL" };
}

/** Formata Money em string localizada (pt-BR) — apenas para exibição */
export function formatMoney(money: Money): string {
  const reais = money.centavos / 100;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: money.currency,
  }).format(reais);
}

/** Soma dois Money de mesma moeda */
export function addMoney(a: Money, b: Money): Money {
  if (a.currency !== b.currency) {
    throw new Error(
      `Não é possível somar Money de moedas diferentes: ${a.currency} e ${b.currency}`,
    );
  }
  return { centavos: a.centavos + b.centavos, currency: a.currency };
}
